import { useMemo } from 'react'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@clouway/ui'

import type { UserSettings } from '../types'

const schema = z.object({
  locale: z.string().min(1),
  zoneinfo: z.string(),
})

export type LocaleFormValues = z.infer<typeof schema>

const LANGUAGES = [
  { value: 'bg', label: 'Български' },
  { value: 'en', label: 'English' },
]

const TIMEZONE_FALLBACK = ['UTC', 'Europe/Sofia', 'Europe/London', 'Europe/Berlin', 'America/New_York']

// The full IANA zone list when the runtime supports it (modern browsers, Node
// 18+); falls back to a short list under happy-dom / older engines.
function supportedTimeZones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }
  try {
    const zones = intl.supportedValuesOf?.('timeZone')
    if (zones && zones.length > 0) return zones
  } catch {
    // fall through to the fallback list
  }
  return TIMEZONE_FALLBACK
}

function detectTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

export function LocaleForm({
  initialValues,
  onSubmit,
}: {
  initialValues: UserSettings
  onSubmit: SubmitHandler<LocaleFormValues>
}) {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const timeZones = useMemo(supportedTimeZones, [])
  const { control, handleSubmit, formState } = useForm<LocaleFormValues>({
    resolver: zodResolver(schema),
    // An unset time zone defaults to the browser's so the field is never blank.
    defaultValues: { locale: initialValues.locale, zoneinfo: initialValues.zoneinfo || detectTimeZone() },
  })

  return (
    <form className="mx-auto flex w-full max-w-2xl flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="locale"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="locale-language">{t('locale.language')}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger id="locale-language">
                <SelectValue placeholder={t('locale.languagePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="zoneinfo"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="locale-timezone">{t('locale.timeZone')}</FieldLabel>
            {/* Native datalist: searchable IANA-zone autocomplete that still
                degrades to a plain text input. */}
            <Input
              id="locale-timezone"
              list="locale-timezones"
              placeholder={t('locale.timeZonePlaceholder')}
              autoComplete="off"
              {...field}
            />
            <datalist id="locale-timezones">
              {timeZones.map((tz) => (
                <option key={tz} value={tz} />
              ))}
            </datalist>
          </Field>
        )}
      />

      <Button type="submit" disabled={formState.isSubmitting}>
        {formState.isSubmitting ? tCommon('ui.button.saving') : tCommon('ui.button.save')}
      </Button>
    </form>
  )
}
